/**
 * Mixin for computing software_service_cost and hardware_cost on agreements.
 *
 * Requires the consuming component to have:
 *   - this.productsFromDB   — full product catalog from /products
 *   - this.alarm_centers    — array of { _id, name } alarm center objects
 *     (or this.orderAlarmCenters, when the component has one - the alarm
 *     centers of the order's country, see admin_order_alarm)
 *
 * Usage:
 *   const contractMonths = Number(this.getContractPeriodValue()) || 0;
 *   const softwareCost = this.computeSoftwareServiceCost(this.order_alarm, contractMonths);
 *   const hardwareCost = this.computeHardwareCost(this.order_alarm);
 */
export default {
  methods: {
    /**
     * Computes total monthly service/software cost over the contract period.
     * Includes: alarm center fee × AC devices, simcard fee × SIM devices,
     *           software product purchase prices × quantity.
     *
     * @param {Object} agreementData  – the form data object (order_alarm / updateInfo / redrawInfo)
     * @param {Number} contractMonths – resolved contract length in months
     * @returns {Number}
     */
    computeSoftwareServiceCost(agreementData, contractMonths) {
      const products = agreementData.products || [];

      // Resolve alarm center name from stored _id
      const alarmCenterName =
        ((this.orderAlarmCenters || this.alarm_centers).find(ac => ac._id === agreementData.alarm_center) || {}).name || '';

      // Find per-unit purchase prices for alarm center and simcard
      const acEntry = this.productsFromDB.find(
        p =>
          p.category === 'alarm_central_supplier' &&
          p.name.trim().toLowerCase() === alarmCenterName.trim().toLowerCase()
      );
      const simEntry = this.productsFromDB.find(
        p =>
          p.category === 'simcard_supplier' &&
          p.name.trim().toLowerCase() === (agreementData.sim_card || '').trim().toLowerCase()
      );

      const acCostPerUnit = acEntry ? acEntry.purchase_price || 0 : 0;
      const simCostPerUnit = simEntry ? simEntry.purchase_price || 0 : 0;

      // Count devices per category rule
      const acCategories = ['alarm'];
      const simCategories = ['alarm', 'vehicle_tracker', 'display'];
      let acDevices = 0;
      let simDevices = 0;
      products.forEach(p => {
        const qty = p.qty || 0;
        if (qty === 0) return;
        if (acCategories.includes(p.category)) acDevices += qty;
        if (simCategories.includes(p.category)) simDevices += qty;
      });

      // Software product purchase prices
      let softwareCost = 0;
      products.forEach(p => {
        const dbProduct =
          this.productsFromDB.find(db => p._id && String(db._id) === String(p._id)) ||
          this.productsFromDB.find(db => p.name && db.name === p.name);
        // A product is treated as software if is_software is true OR category is 'software'
        const isSoftware =
          (dbProduct && (dbProduct.is_software || dbProduct.category === 'software')) ||
          p.is_software || p.category === 'software';
        if (!isSoftware) return;
        const qty = p.qty || 0;
        if (qty > 0) {
          const price = dbProduct ? (dbProduct.purchase_price || 0) : (p.purchase_price || 0);
          softwareCost += qty * price;
        }
        (p.variants || []).forEach(v => {
          const vQty = v.qty || 0;
          if (vQty > 0) softwareCost += vQty * (v.purchase_price || 0);
        });
      });

      return (
        (acCostPerUnit * acDevices + simCostPerUnit * simDevices + softwareCost) * contractMonths
      );
    },

    /**
     * Computes the one-time hardware purchase cost.
     * Includes all non-software products × purchase_price.
     *
     * @param {Object} agreementData – the form data object
     * @returns {Number}
     */
    computeHardwareCost(agreementData) {
      const products = agreementData.products || [];
      let total = 0;
      products.forEach(p => {
        const dbProduct =
          this.productsFromDB.find(db => p._id && String(db._id) === String(p._id)) ||
          this.productsFromDB.find(db => p.name && db.name === p.name);
        // A product is treated as software if is_software is true OR category is 'software'
        const isSoftware =
          (dbProduct && (dbProduct.is_software || dbProduct.category === 'software')) ||
          p.is_software || p.category === 'software';
        if (isSoftware) return;
        const qty = p.qty || 0;
        if (qty > 0) {
          const price = dbProduct ? (dbProduct.purchase_price || 0) : (p.purchase_price || 0);
          total += qty * price;
        }
        (p.variants || []).forEach(v => {
          const vQty = v.qty || 0;
          if (vQty > 0) total += vQty * (v.purchase_price || 0);
        });
      });
      return total;
    },
  },
};
